import { useState } from "react";
import { toast, Toaster } from "react-hot-toast";
import { Dialog } from "@capacitor/dialog";
import JsPlugin from "../JsPlugin";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";

import {
  useChiefComplaintsData,
  useEditComplaintsMutation,
} from "../hooks/useChiefComplaints";
import MainContainer from "../components/Container/MainContainer";
import Card from "../components/Container/Card";
import Button from "../components/Button/Button";
import Microphone from "../components/Icons/Microphone";
import StopWatch from "../components/Icons/StopWatch";
import Play from "../components/Icons/Play";
import Upload from "../components/Icons/Upload";
import EditIcon from "../components/Icons/EditIcon";
import IconButton from "../components/Button/IconButton";

const ChiefComplaints = () => {
  const queryClient = useQueryClient();
  const userId = JSON.parse(localStorage.getItem("user_info"))?.di_id;
  const phoneNumber = JSON.parse(
    localStorage.getItem("user_info")
  )?.phone?.toString();

  const [recordingState, setRecordingState] = useState("idle"); // idle, recording, recorded
  const [audioPath, setAudioPath] = useState("");

  const {
    data: ccTableData = [],
    isLoading: isCCTableDataLoading,
    isError: isCCTableDataError,
    error: ccTableDataError,
  } = useChiefComplaintsData(userId);

  const {
    mutate: editComplaint,
    isLoading: isEditComplaintLoading,
    isError: isEditComplaintError,
    error: editComplaintError,
  } = useEditComplaintsMutation();

  const startRecordAudio = () => {
    setRecordingState("recording");
    JsPlugin.startRecording({ value: "Hello from JavaScript" }).catch(
      (error) => {
        toast.error("রেকর্ডিং শুরু করতে সমস্যা হয়েছে");
        console.error("Recording error:", error);
        setRecordingState("idle");
      }
    );
  };

  const stopRecordAudio = () => {
    JsPlugin.stopRecording({ value: "Hello from JavaScript" })
      .then((result) => {
        setAudioPath(result.message);
        setRecordingState("recorded");
      })
      .catch((error) => {
        toast.error("রেকর্ডিং বন্ধ করতে সমস্যা হয়েছে");
        console.error("Stop recording error:", error);
        setRecordingState("idle");
      });
  };

  const playAudio = () => {
    JsPlugin.playRecording({ value: "play audio" }).catch((error) => {
      toast.error("অডিও চালাতে সমস্যা হয়েছে");
      console.error("Play audio error:", error);
    });
  };

  const uploadAudio = async () => {
    JsPlugin.uploadAudioFile({ value: phoneNumber })
      .then(async (result) => {
        toast.success("আপনার তথ্য সফলভাবে জমা হয়েছে!");
        setRecordingState("idle");
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ["chiefComplaintsData", userId],
          });
        }, 1000);
      })
      .catch((error) => {
        toast.error("আপনার তথ্য জমা দিতে সমস্যা হয়েছে, আবার চেষ্টা করুন");
        console.error("Upload audio error:", error);
      });
  };

  const handleEdit = async (item) => {
    try {
      const { value, cancelled } = await Dialog.prompt({
        title: "সমস্যার বিবরণ সম্পাদনা করুন",
        message: "সমস্যার বিবরণ সম্পাদনা করুন:",
        inputText: item[1],
        okButtonTitle: "সংরক্ষণ করুন",
        cancelButtonTitle: "বাতিল",
      });
      if (!cancelled && value !== null && value !== item[1]) {
        editComplaint({
          id: userId,
          text: value,
          date: item[0],
        });
      }
    } catch (error) {
      toast.error("সম্পাদনা করতে সমস্যা হয়েছে");
      console.error("Edit error:", error);
    }
  };

  const getGuidanceText = () => {
    switch (recordingState) {
      case "recording":
        return "সমস্যা বলা শেষ হলে 'শেষ করুন' বোতামে চাপ দিন।";
      case "recorded":
        return "এবার 'শুনুন' বোতামে চাপ দিয়ে রেকর্ড শুনুন। ঠিক না হলে আবার রেকর্ড করুন।";
      default:
        return "'শুরু করুন' বোতামে চাপ দিয়ে সমস্যা বলা শুরু করুন।";
    }
  };

  if (isCCTableDataLoading) {
    return (
      <MainContainer 
        background="bg-gray-100"
        flex="flex"
        main_axis="justify-center"
        cross_axis="items-center"
      >
        <div className="p-8 bg-white shadow-lg rounded-lg">
          <div className="flex justify-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="mt-4 text-center text-gray-600">লোড হচ্ছে...</p>
        </div>
      </MainContainer>
    );
  }

  if (isCCTableDataError) {
    return (
      <MainContainer
        background="bg-gray-100"
        flex="flex"
        main_axis="justify-center"
        cross_axis="items-center"
      >
        <div className="p-8 bg-white shadow-lg rounded-lg">
          <div className="flex justify-center text-red-500">
            <svg
              className="w-16 h-16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="mt-2 text-xl font-medium text-center text-gray-900">
            একটি সমস্যা হয়েছে
          </h3>
          <p className="mt-2 text-center text-gray-600">
            তথ্য লোড করতে সমস্যা হয়েছে। পুনরায় চেষ্টা করুন।
          </p>
          <div className="mt-4 flex justify-center">
            <button
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ["chiefComplaints"] })
              }
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              আবার চেষ্টা করুন
            </button>
          </div>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer background="bg-gray-50">
      <Toaster position="top-center" />
      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Instructions Card */}
        <Card className="bg-white p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            আপনার সমস্যা বিস্তারিতভাবে বলুন:
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>কতদিন ধরে এই সমস্যা?</li>
            <li>কোনো ডাক্তার দেখিয়েছেন?</li>
            <li>কোনো ওষুধ খাচ্ছেন?</li>
          </ul>

          {/* Guidance Text */}
          <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
            <p className="text-blue-700">{getGuidanceText()}</p>
          </div>
        </Card>

        {/* Recording Controls */}
        <Card className="bg-white p-3 mb-6">
          <div className="grid grid-cols-2 gap-3 mb-4">
            {recordingState !== "recording" ? (
              <Button
                type="button"
                onClick={startRecordAudio}
                disabled={recordingState === "recording"}
                icon={<Microphone />}
                label="শুরু করুন"
                width="w-full"
                rounded="rounded-lg"
                backgroundColor="bg-green-500"
                textColor="text-white"
                hoverBackgroundColor="hover:bg-green-600"
                focusRingColor="focus:ring-green-500"
                focusRingOpacity="focus:ring-opacity-50"
                className="text-xs"
              />
            ) : (
              <Button
                type="button"
                onClick={stopRecordAudio}
                disabled={recordingState !== "recording"}
                icon={<StopWatch />}
                label="শেষ করুন"
                width="w-full"
                rounded="rounded-lg"
                backgroundColor="bg-red-500"
                textColor="text-white"
                hoverBackgroundColor="hover:bg-red-600"
                focusRingColor="focus:ring-red-500"
                focusRingOpacity="focus:ring-opacity-50"
                className="text-xs"
              />
            )}
            <Button
              type="button"
              onClick={playAudio}
              disabled={recordingState !== "recorded"}
              icon={<Play />}
              label="শুনুন"
              width="w-full"
              rounded="rounded-lg"
              backgroundColor="bg-blue-500"
              textColor="text-white"
              hoverBackgroundColor="hover:bg-blue-600"
              focusRingColor="focus:ring-blue-500"
              focusRingOpacity="focus:ring-opacity-50"
              className={`text-xs ${
                recordingState !== "recorded"
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            />
          </div>
          <Button
            type="button"
            onClick={uploadAudio}
            disabled={recordingState !== "recorded"}
            icon={<Upload />}
            label="জমা দিন"
            width="w-full"
            rounded="rounded-lg"
            backgroundColor="bg-purple-600"
            textColor="text-white"
            hoverBackgroundColor="hover:bg-purple-700"
            focusRingColor="focus:ring-purple-500"
            focusRingOpacity="focus:ring-opacity-50"
            className={`text-xs ${
              recordingState !== "recorded"
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          />
        </Card>

        {/* Previous Complaints */}
        {ccTableData.length > 0 && (
          // <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <Card className="bg-white p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              আপনার পূর্ববর্তী সমস্যাসমূহ:
            </h2>
            <div className="divide-y divide-gray-200">
              {ccTableData.map((item, index) => (
                <div key={index} className="py-4">
                  <div className="flex justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        {moment(item[0]).format("DD MMM YYYY")}
                      </span>
                      <p className="mt-1 text-gray-800">{item[1]}</p>
                    </div>
                    <IconButton
                      type="button"
                      onClick={() => handleEdit(item)}
                      icon={<EditIcon />}
                      className="inline-flex items-center p-2 text-sm text-blue-600 hover:text-blue-800"
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </MainContainer>
  );
};

export default ChiefComplaints;
